import React, { useEffect, useState } from 'react'; // Add useState import
import { useSelector, useDispatch } from 'react-redux';
import type { RootState } from 'store';
import type { UserRole } from 'store/authSlice';
import FeatherIcon from 'feather-icons-react';
import type { AppDispatch } from 'store';

import {
  faBoxOpen,
  faCheckCircle,
  faUsers,
  faWarehouse
} from '@fortawesome/free-solid-svg-icons';

import CrmStatCard from 'components/cards/CrmStatCard';

import LeadSources from 'components/list-items/LeadSources';
import NewUsersAndLeads from 'components/modules/crm/NewUsersAndLeads';
import { Col, Row, Button, Badge } from 'react-bootstrap';

// Import thunks for data fetching
import { fetchAssets } from 'store/assetsSlice';
import { fetchUsers } from 'store/userSlice';
import { fetchMyRequests, fetchAllRequests } from 'store/allRequestsSlice';
import { fetchCompanies } from 'store/companySlice';

// Import modals
import CreateAssetModal from 'components/modals/CreateAssetModal';
import CreateUserModal from 'components/modals/CreateUserModal';
import CreateRequestModal from 'components/modals/CreateRequestModal';

const Crm = () => {
  const dispatch = useDispatch<AppDispatch>();

  // Add modal states
  const [openCreateAssetModal, setOpenCreateAssetModal] = useState(false);
  const [openCreateUserModal, setOpenCreateUserModal] = useState(false);
  const [openCreateRequestModal, setOpenCreateRequestModal] = useState(false);

  // Get user data from Redux
  const userRole = useSelector(
    (state: RootState) => state.auth.role as UserRole | undefined
  );
  const userId = useSelector((state: RootState) => state.auth.id);
  const userCompanyId = useSelector((state: RootState) => state.auth.companyId);

  // Get data from Redux store using your existing selectors
  const assets = useSelector((state: RootState) => state.assets.data);
  const users = useSelector((state: RootState) => state.users.data);
  const myRequests = useSelector(
    (state: RootState) => state.allRequests.myRequests
  );
  const allRequests = useSelector(
    (state: RootState) => state.allRequests.requests
  );
  const companies = useSelector((state: RootState) => state.companies.data);

  // Calculate dynamic stats
  const totalAssets = assets.length;
  const availableAssets = assets.filter(
    asset => asset.status === 'available'
  ).length;
  const inUseAssets = assets.filter(
    asset => asset.status === 'assigned'
  ).length;
  const totalUsers = users.length;

  // For user view - devices assigned to current user
  const myDevices = assets.filter(asset => asset.current_holder_id === userId);
  const myPendingRequests = myRequests.filter(
    request => request.status === 'pending'
  );
  const myApprovedRequests = myRequests.filter(
    request => request.status === 'approved'
  );

  // Fetch data on component mount
  // Fetch data on component mount
  useEffect(() => {
    if (userRole === 'super_user') {
      // Super user can see everything
      dispatch(fetchAssets());
      dispatch(fetchUsers());
      dispatch(fetchAllRequests());
      dispatch(fetchCompanies());
    } else if (userRole === 'company_admin' && userCompanyId) {
      // Company admin sees company-specific data
      dispatch(fetchAssets());
      dispatch(fetchUsers({ company_id: parseInt(userCompanyId) }));
      dispatch(fetchAllRequests());
      dispatch(fetchCompanies()); // ADD THIS LINE - fetch companies for company admin
    } else if (userRole === 'user') {
      // Regular user sees their data
      dispatch(fetchAssets());
      dispatch(fetchMyRequests());
    }
  }, [dispatch, userRole, userCompanyId]);

  if (userRole === 'super_user') {
    return (
      <>
        {/* 🔝 Top Header */}
        <Row className="align-items-center justify-content-between mb-4">
          <Col>
            <h2 className="mb-0">🏠 IT Device Management (Super User View)</h2>
          </Col>
        </Row>

        {/* 📊 Quick Stats Cards */}
        <Row className="g-3 mb-4">
          <Col sm={6} xl={3}>
            <CrmStatCard
              icon={faBoxOpen}
              color="primary"
              label="Total Assets"
              value={totalAssets}
              title="All Companies"
              badgeLabel={`${companies.length} companies`}
              badgeBg="success"
              footerText="Across all organizations"
            />
          </Col>
          <Col sm={6} xl={3}>
            <CrmStatCard
              icon={faCheckCircle}
              color="success"
              label="In Use Devices"
              value={inUseAssets}
              title="Currently Active"
              badgeLabel={`${availableAssets} available`}
              badgeBg="info"
              footerText="Devices in circulation"
            />
          </Col>
          <Col sm={6} xl={3}>
            <CrmStatCard
              icon={faWarehouse}
              color="warning"
              label="In Stock"
              value={availableAssets}
              title="Available Now"
              badgeLabel={`${((availableAssets / totalAssets) * 100).toFixed(
                1
              )}%`}
              badgeBg="warning"
              footerText="Ready for assignment"
            />
          </Col>
          <Col sm={6} xl={3}>
            <CrmStatCard
              icon={faUsers}
              color="info"
              label="Users"
              value={totalUsers}
              title="Active Users"
              badgeLabel={`${allRequests.length} requests`}
              badgeBg="primary"
              footerText="Total system users"
            />
          </Col>
        </Row>

        {/* 📋 Recent Activity & Department Overview */}
        <Row className="g-4 mb-5">
          <Col>
            <div className="border rounded p-3 h-100">
              <h5 className="mb-3">Recent Activity</h5>
              <NewUsersAndLeads />
            </div>
          </Col>
        </Row>

        {/* 📂 Bottom Blocks */}
        <Row className="g-4">
          <Col md={6} xl={4}>
            <div className="border rounded-3 p-4 h-100 bg-white shadow-sm">
              <div className="d-flex align-items-center mb-3">
                <div
                  className="bg-primary bg-opacity-10 rounded-circle p-2 me-2 d-flex align-items-center justify-content-center"
                  style={{ width: '40px', height: '40px' }}
                >
                  <FeatherIcon
                    icon="activity"
                    size={16}
                    className="text-primary"
                  />
                </div>
                <h5 className="mb-0">Top Users by Devices</h5>
              </div>
              <div className="mb-2">
                <p className="text-body-tertiary fs-9 mb-2">
                  Users with most assigned devices
                </p>
              </div>
              <LeadSources />
            </div>
          </Col>

          <Col md={6} xl={4}>
            <div className="border rounded-3 p-4 h-100 bg-white shadow-sm">
              <div className="d-flex align-items-center mb-3">
                <div
                  className="bg-success bg-opacity-10 rounded-circle p-2 me-2 d-flex align-items-center justify-content-center"
                  style={{ width: '40px', height: '40px' }}
                >
                  <FeatherIcon icon="zap" size={16} className="text-success" />
                </div>
                <h5 className="mb-0">Quick Actions</h5>
              </div>
              <div className="mb-3">
                <p className="text-body-tertiary fs-9 mb-3">
                  Common tasks and quick operations
                </p>
              </div>
              <div className="d-grid gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  className="d-flex align-items-center justify-content-center gap-2 py-2"
                  onClick={() => setOpenCreateAssetModal(true)}
                >
                  <FeatherIcon icon="plus" size={16} />
                  Add New Device
                </Button>
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="d-flex align-items-center justify-content-center gap-2 py-2"
                  onClick={() => setOpenCreateUserModal(true)}
                >
                  <FeatherIcon icon="user-plus" size={16} />
                  Add New User
                </Button>
              </div>
            </div>
          </Col>

          <Col md={6} xl={4}>
            <div className="border rounded-3 p-4 h-100 bg-white shadow-sm">
              <div className="d-flex align-items-center mb-3">
                <div
                  className="bg-info bg-opacity-10 rounded-circle p-2 me-2 d-flex align-items-center justify-content-center"
                  style={{ width: '40px', height: '40px' }}
                >
                  <FeatherIcon
                    icon="bar-chart-2"
                    size={16}
                    className="text-info"
                  />
                </div>
                <h5 className="mb-0">Quick Stats</h5>
              </div>
              <div className="mb-3">
                <p className="text-body-tertiary fs-9 mb-3">
                  At-a-glance overview
                </p>
              </div>
              <div className="d-grid gap-3">
                <div className="d-flex justify-content-between align-items-center p-2 bg-light rounded">
                  <span className="fs-9 text-body">Available Devices</span>
                  <Badge bg="success" className="fs-10">
                    {availableAssets}
                  </Badge>
                </div>
                <div className="d-flex justify-content-between align-items-center p-2 bg-light rounded">
                  <span className="fs-9 text-body">Pending Requests</span>
                  <Badge bg="warning" className="fs-10">
                    {allRequests.filter(req => req.status === 'pending').length}
                  </Badge>
                </div>
                <div className="d-flex justify-content-between align-items-center p-2 bg-light rounded">
                  <span className="fs-9 text-body">Active Users</span>
                  <Badge bg="info" className="fs-10">
                    {users.filter(user => user.is_active).length}
                  </Badge>
                </div>
              </div>
            </div>
          </Col>
        </Row>

        {/* Enhanced Company Comparison for Superuser */}
        {companies.length > 0 && (
          <div className="mt-5">
            <div className="d-flex align-items-center mb-4">
              <h4 className="mb-0">🏢 Company Comparison</h4>
            </div>
            <Row className="g-4">
              {companies.slice(0, 3).map(company => {
                const companyAssets = assets.filter(
                  asset => asset.company_id === company.id
                );
                const companyUsers = users.filter(
                  user => user.company_id === company.id
                );
                const companyInUseAssets = companyAssets.filter(
                  asset => asset.status === 'assigned'
                ).length;
                const companyAvailableAssets = companyAssets.filter(
                  asset => asset.status === 'available'
                ).length;

                return (
                  <Col md={4} key={company.id}>
                    <div className="border rounded-3 p-4 h-100 bg-white shadow-sm">
                      <div className="d-flex align-items-center mb-3">
                        <div
                          className="bg-primary bg-opacity-10 rounded-circle p-2 me-2 d-flex align-items-center justify-content-center"
                          style={{ width: '40px', height: '40px' }}
                        >
                          <FeatherIcon
                            icon="grid"
                            size={16}
                            className="text-primary"
                          />
                        </div>
                        <h6 className="mb-0 text-truncate">{company.name}</h6>
                      </div>
                      <div className="d-grid gap-2">
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="fs-9 text-body-tertiary">
                            Total Assets
                          </span>
                          <strong className="text-body-emphasis">
                            {companyAssets.length}
                          </strong>
                        </div>
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="fs-9 text-body-tertiary">
                            Active Users
                          </span>
                          <strong className="text-body-emphasis">
                            {companyUsers.length}
                          </strong>
                        </div>
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="fs-9 text-body-tertiary">
                            In Use
                          </span>
                          <Badge bg="success" className="fs-10">
                            {companyInUseAssets}
                          </Badge>
                        </div>
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="fs-9 text-body-tertiary">
                            Available
                          </span>
                          <Badge bg="info" className="fs-10">
                            {companyAvailableAssets}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>
          </div>
        )}

        {/* Add Modals */}
        <CreateAssetModal
          show={openCreateAssetModal}
          handleClose={() => setOpenCreateAssetModal(false)}
        />
        <CreateUserModal
          show={openCreateUserModal}
          handleClose={() => setOpenCreateUserModal(false)}
        />
      </>
    );
  }

  if (userRole === 'company_admin') {
    // Filter data for company admin's company only
    const companyAssets = assets.filter(
      asset => asset.company_id === parseInt(userCompanyId || '')
    );
    const companyUsers = users.filter(
      user => user.company_id === parseInt(userCompanyId || '')
    );
    const companyAvailableAssets = companyAssets.filter(
      asset => asset.status === 'available'
    ).length;
    const companyInUseAssets = companyAssets.filter(
      asset => asset.status === 'assigned'
    ).length;

    // Get company name from companies data - add fallback to auth state
    const currentCompany = companies.find(
      company => company.id === parseInt(userCompanyId || '')
    );
    // Try multiple fallbacks for company name
    const companyName = currentCompany?.name || 'Your Company'; // Final fallback

    return (
      <>
        {/* 🔝 Top Header */}
        <Row className="align-items-center justify-content-between mb-4">
          <Col>
            <h2 className="mb-0">🏠 {companyName} - Device Management</h2>
          </Col>
        </Row>

        {/* 📊 Quick Stats Cards */}
        <Row className="g-3 mb-4">
          <Col sm={6} xl={3}>
            <CrmStatCard
              icon={faBoxOpen}
              color="primary"
              label="Total Assets"
              value={companyAssets.length}
              title="Company Assets"
              badgeLabel={`${allRequests.length} requests`}
              badgeBg="success"
              footerText="Total company inventory"
            />
          </Col>
          <Col sm={6} xl={3}>
            <CrmStatCard
              icon={faCheckCircle}
              color="success"
              label="In Use Devices"
              value={companyInUseAssets}
              title="Currently Active"
              badgeLabel={`${companyAvailableAssets} available`}
              badgeBg="info"
              footerText="Devices in circulation"
            />
          </Col>
          <Col sm={6} xl={3}>
            <CrmStatCard
              icon={faWarehouse}
              color="warning"
              label="In Stock"
              value={companyAvailableAssets}
              title="Available Now"
              badgeLabel={
                companyAssets.length > 0
                  ? `${(
                      (companyAvailableAssets / companyAssets.length) *
                      100
                    ).toFixed(1)}%`
                  : '0%'
              }
              badgeBg="warning"
              footerText="Ready for assignment"
            />
          </Col>
          <Col sm={6} xl={3}>
            <CrmStatCard
              icon={faUsers}
              color="info"
              label="Users"
              value={companyUsers.length}
              title="Company Users"
              badgeLabel={`${myRequests.length} my requests`}
              badgeBg="primary"
              footerText="Active company users"
            />
          </Col>
        </Row>

        {/* 📋 Recent Activity & Department Overview */}
        <Row className="g-4 mb-5">
          <Col>
            <div className="border rounded p-3 h-100">
              <h5 className="mb-3">Recent Activity</h5>
              <NewUsersAndLeads />
            </div>
          </Col>
        </Row>

        {/* 📂 Bottom Blocks - Updated to match super admin UI */}
        <Row className="g-4">
          <Col md={6} xl={4}>
            <div className="border rounded-3 p-4 h-100 bg-white shadow-sm">
              <div className="d-flex align-items-center mb-3">
                <div
                  className="bg-primary bg-opacity-10 rounded-circle p-2 me-2 d-flex align-items-center justify-content-center"
                  style={{ width: '40px', height: '40px' }}
                >
                  <FeatherIcon
                    icon="activity"
                    size={16}
                    className="text-primary"
                  />
                </div>
                <h5 className="mb-0">Top Users by Devices</h5>
              </div>
              <div className="mb-2">
                <p className="text-body-tertiary fs-9 mb-2">
                  Users with most assigned devices
                </p>
              </div>
              <LeadSources />
            </div>
          </Col>

          <Col md={6} xl={4}>
            <div className="border rounded-3 p-4 h-100 bg-white shadow-sm">
              <div className="d-flex align-items-center mb-3">
                <div
                  className="bg-success bg-opacity-10 rounded-circle p-2 me-2 d-flex align-items-center justify-content-center"
                  style={{ width: '40px', height: '40px' }}
                >
                  <FeatherIcon icon="zap" size={16} className="text-success" />
                </div>
                <h5 className="mb-0">Quick Actions</h5>
              </div>
              <div className="mb-3">
                <p className="text-body-tertiary fs-9 mb-3">
                  Common tasks and quick operations
                </p>
              </div>
              <div className="d-grid gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  className="d-flex align-items-center justify-content-center gap-2 py-2"
                  onClick={() => setOpenCreateAssetModal(true)}
                >
                  <FeatherIcon icon="plus" size={16} />
                  Add New Device
                </Button>
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="d-flex align-items-center justify-content-center gap-2 py-2"
                  onClick={() => setOpenCreateUserModal(true)}
                >
                  <FeatherIcon icon="user-plus" size={16} />
                  Add New User
                </Button>
              </div>
            </div>
          </Col>

          <Col md={6} xl={4}>
            <div className="border rounded-3 p-4 h-100 bg-white shadow-sm">
              <div className="d-flex align-items-center mb-3">
                <div
                  className="bg-info bg-opacity-10 rounded-circle p-2 me-2 d-flex align-items-center justify-content-center"
                  style={{ width: '40px', height: '40px' }}
                >
                  <FeatherIcon
                    icon="bar-chart-2"
                    size={16}
                    className="text-info"
                  />
                </div>
                <h5 className="mb-0">Quick Stats</h5>
              </div>
              <div className="mb-3">
                <p className="text-body-tertiary fs-9 mb-3">
                  At-a-glance overview
                </p>
              </div>
              <div className="d-grid gap-3">
                <div className="d-flex justify-content-between align-items-center p-2 bg-light rounded">
                  <span className="fs-9 text-body">Available Devices</span>
                  <Badge bg="success" className="fs-10">
                    {companyAvailableAssets}
                  </Badge>
                </div>
                <div className="d-flex justify-content-between align-items-center p-2 bg-light rounded">
                  <span className="fs-9 text-body">Pending Requests</span>
                  <Badge bg="warning" className="fs-10">
                    {allRequests.filter(req => req.status === 'pending').length}
                  </Badge>
                </div>
                <div className="d-flex justify-content-between align-items-center p-2 bg-light rounded">
                  <span className="fs-9 text-body">Active Users</span>
                  <Badge bg="info" className="fs-10">
                    {companyUsers.filter(user => user.is_active).length}
                  </Badge>
                </div>
              </div>
            </div>
          </Col>
        </Row>

        {/* ADD MODALS FOR COMPANY ADMIN - This was missing! */}
        <CreateAssetModal
          show={openCreateAssetModal}
          handleClose={() => setOpenCreateAssetModal(false)}
        />
        <CreateUserModal
          show={openCreateUserModal}
          handleClose={() => setOpenCreateUserModal(false)}
        />
      </>
    );
  }

  if (userRole === 'user') {
    // Get company name for user view
    const currentCompany = companies.find(
      company => company.id === parseInt(userCompanyId || '')
    );
    const companyName = currentCompany?.name || 'Your Company';

    return (
      <>
        {/* 🔝 Top Header */}
        <Row className="align-items-center justify-content-between mb-4">
          <Col>
            <h2 className="mb-0">🏠 {companyName} - My Dashboard</h2>
          </Col>
        </Row>

        {/* 📂 Bottom Blocks - Similar to Admin UI */}
        <Row className="g-4">
          <Col md={6} xl={4}>
            <div className="border rounded-3 p-4 h-100 bg-white shadow-sm">
              <div className="d-flex align-items-center mb-3">
                <div
                  className="bg-primary bg-opacity-10 rounded-circle p-2 me-2 d-flex align-items-center justify-content-center"
                  style={{ width: '40px', height: '40px' }}
                >
                  <FeatherIcon
                    icon="monitor"
                    size={16}
                    className="text-primary"
                  />
                </div>
                <h5 className="mb-0">My Devices</h5>
              </div>
              <div className="mb-2">
                <p className="text-body-tertiary fs-9 mb-2">
                  Devices currently assigned to you
                </p>
              </div>
              <ul className="list-unstyled mb-0">
                {myDevices.slice(0, 5).map(device => (
                  <li
                    key={device.id}
                    className="d-flex justify-content-between align-items-center mb-2 p-2 bg-light rounded"
                  >
                    <span className="fs-9">💻 {device.name}</span>
                    <Badge
                      bg={device.status === 'assigned' ? 'success' : 'warning'}
                      className="fs-10"
                    >
                      {device.status === 'assigned' ? 'Active' : 'Maintenance'}
                    </Badge>
                  </li>
                ))}
                {myDevices.length === 0 && (
                  <li className="text-body-secondary p-2">
                    No devices assigned
                  </li>
                )}
              </ul>
            </div>
          </Col>

          <Col md={6} xl={4}>
            <div className="border rounded-3 p-4 h-100 bg-white shadow-sm">
              <div className="d-flex align-items-center mb-3">
                <div
                  className="bg-success bg-opacity-10 rounded-circle p-2 me-2 d-flex align-items-center justify-content-center"
                  style={{ width: '40px', height: '40px' }}
                >
                  <FeatherIcon icon="zap" size={16} className="text-success" />
                </div>
                <h5 className="mb-0">Quick Actions</h5>
              </div>
              <div className="mb-3">
                <p className="text-body-tertiary fs-9 mb-3">
                  Common tasks and quick operations
                </p>
              </div>
              <div className="d-grid gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  className="d-flex align-items-center justify-content-center gap-2 py-2"
                  onClick={() => setOpenCreateRequestModal(true)}
                >
                  <FeatherIcon icon="plus" size={16} />
                  Request New Device
                </Button>
              </div>
            </div>
          </Col>

          <Col md={6} xl={4}>
            <div className="border rounded-3 p-4 h-100 bg-white shadow-sm">
              <div className="d-flex align-items-center mb-3">
                <div
                  className="bg-info bg-opacity-10 rounded-circle p-2 me-2 d-flex align-items-center justify-content-center"
                  style={{ width: '40px', height: '40px' }}
                >
                  <FeatherIcon
                    icon="bar-chart-2"
                    size={16}
                    className="text-info"
                  />
                </div>
                <h5 className="mb-0">Request Status</h5>
              </div>
              <div className="mb-3">
                <p className="text-body-tertiary fs-9 mb-3">
                  Overview of your requests
                </p>
              </div>
              <div className="d-grid gap-3">
                <div className="d-flex justify-content-between align-items-center p-2 bg-light rounded">
                  <span className="fs-9 text-body">Pending Requests</span>
                  <Badge bg="warning" className="fs-10">
                    {myPendingRequests.length}
                  </Badge>
                </div>
                <div className="d-flex justify-content-between align-items-center p-2 bg-light rounded">
                  <span className="fs-9 text-body">Approved Requests</span>
                  <Badge bg="success" className="fs-10">
                    {myApprovedRequests.length}
                  </Badge>
                </div>
                <div className="d-flex justify-content-between align-items-center p-2 bg-light rounded">
                  <span className="fs-9 text-body">Total Requests</span>
                  <Badge bg="info" className="fs-10">
                    {myRequests.length}
                  </Badge>
                </div>
                <div className="d-flex justify-content-between align-items-center p-2 bg-light rounded">
                  <span className="fs-9 text-body">Active Devices</span>
                  <Badge bg="primary" className="fs-10">
                    {
                      myDevices.filter(device => device.status === 'assigned')
                        .length
                    }
                  </Badge>
                </div>
              </div>
            </div>
          </Col>
        </Row>

        {/* Add Request Modal */}
        {openCreateRequestModal && (
          <CreateRequestModal
            show={openCreateRequestModal}
            handleClose={() => setOpenCreateRequestModal(false)}
          />
        )}
      </>
    );
  }

  // If user role is not recognized or undefined
  return (
    <div className="text-center mt-5">
      <h3>Unable to determine your access level.</h3>
    </div>
  );
};

export default Crm;
